/**
 * Quality auto-scaler (pure). Fed every frame with the frame's CPU time and its rAF interval,
 * it steps the quality preset down one level when frames stay over budget, and back up (never
 * above the player's chosen preset) after a long stretch with plenty of headroom.
 *
 * Two ways to be over budget:
 *  - CPU: the 90th percentile of frame CPU time over the window exceeds `cpuBudgetMs`;
 *  - GPU / compositor: frames arrive late (median interval > 1.45× the display's refresh
 *    interval, estimated as the window's fast frames) although the CPU part is small.
 * Works at any refresh rate (60, 120, 144 Hz): nothing assumes 16.7 ms.
 */
import type { QualityPreset } from './settings';

const ORDER: readonly QualityPreset[] = ['low', 'medium', 'high'];
/**
 * Lowest preset the scaler picks on its own. 'low' turns shadows off, which recompiles every
 * material (a long hitch mid-fight), so only the player can choose it.
 */
export const AUTO_FLOOR: QualityPreset = 'medium';

export interface AutoQualityOptions {
  /** CPU budget per frame (ms). */
  cpuBudgetMs?: number;
  /** Frames per evaluation window. */
  window?: number;
  /** Over budget for this long (ms of samples) before stepping down. */
  downAfterMs?: number;
  /** Comfortably under budget for this long before stepping back up. */
  upAfterMs?: number;
}

function pct(sorted: number[], p: number): number {
  return sorted.length ? sorted[Math.min(sorted.length - 1, Math.max(0, Math.ceil((p / 100) * sorted.length) - 1))]! : NaN;
}

export class AutoQuality {
  readonly cpuBudgetMs: number;
  private readonly win: number;
  private readonly downAfterMs: number;
  private readonly upAfterMs: number;
  private cpu: number[] = [];
  private dt: number[] = [];
  private overMs = 0;
  private underMs = 0;
  /** Step-downs so far: each one doubles the wait before trying a higher preset again (no flapping). */
  private downs = 0;
  /** Current preset (≤ ceiling). */
  current: QualityPreset;
  /** Last reason a change happened (debug overlay). */
  reason = '';

  constructor(
    /** The player's chosen preset: never exceeded. */
    public ceiling: QualityPreset,
    opts: AutoQualityOptions = {},
  ) {
    this.current = ceiling;
    this.cpuBudgetMs = opts.cpuBudgetMs ?? 8;
    this.win = opts.window ?? 90;
    this.downAfterMs = opts.downAfterMs ?? 2000;
    this.upAfterMs = opts.upAfterMs ?? 15000;
  }

  /** The player changed the preset: reset to it. */
  setCeiling(q: QualityPreset): void {
    this.ceiling = q;
    this.current = q;
    this.downs = 0;
    this.reset();
  }

  reset(): void {
    this.cpu = [];
    this.dt = [];
    this.overMs = 0;
    this.underMs = 0;
  }

  /** Feed one frame. Returns the new preset when it changed, else null. */
  sample(cpuMs: number, dtMs: number): QualityPreset | null {
    if (!(dtMs > 0) || dtMs > 250) return null; // tab switch / hitch: not representative
    this.cpu.push(cpuMs);
    this.dt.push(dtMs);
    if (this.cpu.length > this.win) {
      this.cpu.shift();
      this.dt.shift();
    }
    if (this.cpu.length < this.win) return null;
    const c = [...this.cpu].sort((a, b) => a - b);
    const d = [...this.dt].sort((a, b) => a - b);
    const refresh = pct(d, 10);
    const cpu90 = pct(c, 90);
    const cpuOver = cpu90 > this.cpuBudgetMs;
    const gpuOver = pct(d, 50) > refresh * 1.45 && cpu90 < refresh * 0.8;
    const roomy = cpu90 < this.cpuBudgetMs * 0.55 && pct(d, 50) < refresh * 1.15;
    this.overMs = cpuOver || gpuOver ? this.overMs + dtMs : 0;
    this.underMs = roomy ? this.underMs + dtMs : 0;
    const i = ORDER.indexOf(this.current);
    if (this.overMs >= this.downAfterMs && i > ORDER.indexOf(AUTO_FLOOR)) {
      this.current = ORDER[i - 1]!;
      this.downs++;
      this.reason = cpuOver ? `cpu p90 ${cpu90.toFixed(1)} ms > ${this.cpuBudgetMs}` : `frames late (${pct(d, 50).toFixed(1)} ms vs ${refresh.toFixed(1)} ms refresh)`;
      this.reset();
      return this.current;
    }
    if (this.underMs >= this.upAfterMs * 2 ** Math.min(4, this.downs) && i < ORDER.indexOf(this.ceiling)) {
      this.current = ORDER[i + 1]!;
      this.reason = `headroom (cpu p90 ${cpu90.toFixed(1)} ms)`;
      this.reset();
      return this.current;
    }
    return null;
  }
}
